import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  getTranslationFromCache,
  setCachedTranslation,
  preloadOnIdle,
  LANGUAGE_CONFIG,
  DEFAULT_LANGUAGE,
  getBestMatchingLanguage,
} from "../utils/translation";
import { generateStarterMsg } from "../utils/botSatrter";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "../utils/localStorage";
import { isRTLLanguage, getTextDirection } from "../utils/rtlUtils";

interface Translations {
  [key: string]: string;
}

interface TranslationState {
  language: string;
  data: Translations;
}

// Helper to get browser languages based on bot type
const getBrowserLanguages = (
  config: any,
  isIndependentBot: boolean
): string[] => {
  if (isIndependentBot) {
    return navigator.languages
      ? Array.from(navigator.languages)
      : [navigator.language || DEFAULT_LANGUAGE];
  }

  const configBrowserLanguages = config?.browserLanguages;
  if (configBrowserLanguages) {
    return Array.isArray(configBrowserLanguages)
      ? configBrowserLanguages
      : [configBrowserLanguages];
  }

  return navigator.languages
    ? Array.from(navigator.languages)
    : [navigator.language || DEFAULT_LANGUAGE];
};

// Function to detect browser language with fallback
const detectBrowserLanguage = (
  config: any,
  supportedLanguages: string[],
  isIndependentBot: boolean = false
): string => {
  const browserLanguages = getBrowserLanguages(config, isIndependentBot);

  // Try browser languages first
  const browserMatch = getBestMatchingLanguage(
    browserLanguages,
    supportedLanguages
  );
  if (browserMatch !== DEFAULT_LANGUAGE) {
    return browserMatch;
  }

  // Try config's current language
  if (config?.current_language) {
    if (supportedLanguages?.includes(config.current_language)) {
      return config.current_language;
    }
  }

  return DEFAULT_LANGUAGE;
};

interface TranslationContextValue {
  t: (key: string, vars?: Record<string, string | number>) => string;
  handleLanguageChange: ({
    newLanguage,
    initializeData,
  }: {
    newLanguage: string;
    initializeData?: any;
  }) => void;
  initializeTranslations: (configData: any) => any[];
  currentLanguage: string;
  loading: boolean;
  supportedLanguages: string[];
  initialDetectedLanguage: string;
  isInitialized: boolean;
  configTranslations: any;
  isRTL: boolean;
  direction: "rtl" | "ltr";
}

const TranslationContext = createContext<TranslationContextValue | undefined>(
  undefined
);

interface TranslationProviderProps {
  children: ReactNode;
  config: any;
  setConfig: (config: any) => void;
  isIndependentBot: boolean;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
  children,
  config,
  setConfig,
  isIndependentBot,
}) => {
  // Use lazy state initialization and avoid depending on config immediately
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([
    DEFAULT_LANGUAGE,
  ]);
  const [initialDetectedLanguage, setInitialDetectedLanguage] =
    useState<string>(DEFAULT_LANGUAGE);
  const [isConfigInitialized, setIsConfigInitialized] =
    useState<boolean>(false);
  const [configTranslations, setConfigTranslations] = useState<any>({
    language: "",
    data: {},
  });
  const [translations, setTranslations] = useState<TranslationState>({
    language: "",
    data: {},
  });
  const [language, setLanguage] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Compute RTL state based on current language
  const isRTL = isRTLLanguage(language);
  const direction = getTextDirection(language);

  // Initialization now happens via explicit initializeTranslations call

  // Function to initialize translations with complete config data
  const initializeTranslations = (initialData: any) => {
    const configData = initialData.config;
    if (
      !configData?.supported_languages ||
      configData.supported_languages.length === 0
    ) {
      console.warn(
        "Cannot initialize translations: supported_languages not provided"
      );
      return []; // Return empty array to satisfy return type
    }

    const newSupportedLanguages = configData.supported_languages;
    setSupportedLanguages(newSupportedLanguages);

    // Detect browser language with the updated supported languages
    const detectedLanguage = detectBrowserLanguage(
      configData,
      newSupportedLanguages,
      isIndependentBot
    );
    setInitialDetectedLanguage(detectedLanguage);

    // Determine the language to use for starter messages
    let currentLang = language ?? DEFAULT_LANGUAGE;
    const enableLanguageToggle = initialData?.enableLanguageToggle;
    if (!language) {
      if (enableLanguageToggle) {
        const persistedLanguage = getLocalStorageItem({
          botId: configData?.botId,
          filterId: configData?.filterId,
          key: "preferred_language",
        });
        currentLang =
          persistedLanguage && newSupportedLanguages.includes(persistedLanguage)
            ? persistedLanguage
            : detectedLanguage;
      } else {
        currentLang = detectedLanguage;
      }

      // Set the language and config translations
      setLanguage(currentLang);
      const initialConfigTranslations = configData.translations[currentLang];
      setConfigTranslations({
        language: currentLang,
        data: initialConfigTranslations,
      });
    }

    // Always generate starter messages with the determined language
    const starterMsg = generateStarterMsg(initialData, currentLang);

    setIsConfigInitialized(true);
    return starterMsg; // Always return starter messages
  };

  // Persist language to localStorage with bot-specific key
  const persistLanguage = (lang: string) => {
    setLocalStorageItem({
      botId: config?.botId,
      filterId: config?.filterId,
      key: "preferred_language",
      value: lang,
    });
  };

  // Update config with initializeData - separate from state updates
  const updateConfigWithTranslations = (
    normalizedLanguage: string,
    initializeData?: any
  ) => {
    if (initializeData?.config?.translations?.[normalizedLanguage]) {
      setConfigTranslations({
        language: normalizedLanguage,
        data: initializeData?.config?.translations?.[normalizedLanguage],
      });
    }
  };

  const handleLanguageChange = ({
    newLanguage,
    initializeData,
  }: {
    newLanguage: string;
    initializeData?: any;
  }) => {
    // Update supported languages if provided in initializeData
    const _supportedLanguages = initializeData?.config?.supported_languages;
    if (_supportedLanguages && _supportedLanguages.length > 0) {
      const areLanguagesDifferent =
        supportedLanguages.length !== _supportedLanguages.length ||
        !supportedLanguages.every(
          (lang, index) => lang === _supportedLanguages[index]
        );

      if (areLanguagesDifferent) {
        setSupportedLanguages(_supportedLanguages);
      }
    }

    // Use current supported languages or the new ones from initializeData
    const currentSupportedLanguages = _supportedLanguages || supportedLanguages;

    if (!currentSupportedLanguages.includes(newLanguage)) {
      console.warn(`Language ${newLanguage} is not supported`);
      return;
    }

    if (newLanguage !== language) {
      setLanguage(newLanguage);
      persistLanguage(newLanguage);
      updateConfigWithTranslations(newLanguage, initializeData);
    } else if (
      config?.current_language &&
      config?.current_language !== newLanguage
    ) {
      // Same language but sync initializeData
      updateConfigWithTranslations(newLanguage, initializeData);
    }
  };

  // Load translations with caching
  const loadTranslations = async (targetLanguage: string) => {
    try {
      setLoading(true);

      // Check cache first
      const cachedTranslations = getTranslationFromCache(targetLanguage);
      if (cachedTranslations) {
        setTranslations({
          language: targetLanguage,
          data: cachedTranslations,
        });
        return;
      }

      // Get filename - simplified logic
      const filename = LANGUAGE_CONFIG[targetLanguage]?.filename || "en";
      const translationsData = await import(`../locales/${filename}.json`);

      // Cache and set translations
      setCachedTranslation(targetLanguage, translationsData);
      setTranslations({
        language: targetLanguage,
        data: translationsData,
      });
    } catch (error) {
      console.error("Error loading translation file:", error);
      if (targetLanguage !== DEFAULT_LANGUAGE) {
        // Load default language translations but keep the user's language preference
        loadTranslations(DEFAULT_LANGUAGE);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load translations when language changes
  useEffect(() => {
    if (language && translations.language !== language) {
      loadTranslations(language);
    }
  }, [language]);

  // Initial setup on mount - only preload, actual initialization happens via initializeTranslations
  useEffect(() => {
    preloadOnIdle();
  }, []);

  const t = (key: string, vars?: Record<string, string | number>): string => {
    const staticContent = configTranslations?.data?.staticContent || {};
    let template = staticContent[key] || translations.data[key] || key;

    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        template = template.replace(new RegExp(`{${k}}`, "g"), String(v));
      });
    }
    return template;
  };
  return (
    <TranslationContext.Provider
      value={{
        t,
        handleLanguageChange,
        initializeTranslations,
        currentLanguage: language,
        loading,
        supportedLanguages,
        initialDetectedLanguage,
        isInitialized: isConfigInitialized,
        configTranslations,
        isRTL,
        direction,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextValue => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};
